import {type TimelineItem, TimelineItemType} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {
  BlockType,
  FilterOperator,
  FilterQueryParser,
  defaultFilterConfig,
  escapeFilterValue,
  getUnescapedFilterValue,
  isFilterBlock,
} from '@github-ui/filter-core'
import {SelectFilterProvider, StaticFilterProvider} from '@github-ui/filter-core/providers'
import {ClockIcon, ListUnorderedIcon, PersonIcon} from '@primer/octicons-react'
import type {TimelineActivityGroupKey, TimelinePanelMetadata} from '../../../contexts/DockedPanelContext'
import {SinceSource} from '../../../routes/route-payload-types'
import {getTimelineItemAutomation} from './copilot-work-automation'

export function groupPredicate(group: TimelineActivityGroupKey): (item: TimelineItem) => boolean {
  switch (group) {
    case 'commits':
      return item => item.additionalInfo?.info?.case === 'pullRequestCommit'
    case 'baseForce':
      return item => {
        const info = item.additionalInfo?.info
        return (
          info?.case === 'issueEvent' &&
          ['head_ref_force_pushed', 'base_ref_force_pushed', 'base_ref_changed'].includes(info.value.eventType)
        )
      }
    case 'approvals':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'pullRequestReview' && info.value.state === 'approved'
      }
    case 'changesRequested':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'pullRequestReview' && info.value.state === 'changes_requested'
      }
    case 'comments':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'pullRequestReview' && info.value.state === 'commented'
      }
    case 'issueComments':
      return item => item.additionalInfo?.info?.case === 'issueComment'
    case 'state':
      return item => {
        const info = item.additionalInfo?.info
        return (
          info?.case === 'issueEvent' &&
          ['closed', 'reopened', 'merged', 'convert_to_draft', 'ready_for_review'].includes(info.value.eventType)
        )
      }
    case 'labels':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'issueEvent' && ['labeled', 'unlabeled'].includes(info.value.eventType)
      }
    case 'reviewRequests':
      return item => {
        const info = item.additionalInfo?.info
        return (
          info?.case === 'issueEvent' && ['review_requested', 'review_request_removed'].includes(info.value.eventType)
        )
      }
    case 'description':
      return item => item.type === TimelineItemType.ISSUE_EDIT
    case 'copilotActivity':
      return item => matchesActor(item, 'copilot')
  }
}

const copilotActorDisplayLogins = new Set(['copilot', 'copilot code review'])

// Shared by the `actor:` qualifier and the `copilotActivity` group predicate so
// both agree on what counts as a match.
function matchesActor(item: TimelineItem, value: string): boolean {
  const normalizedValue = value.toLowerCase()
  // The displayed Copilot performer may be acting for a human or organization.
  // Catch-up counts still classify the recorded actor, not that presentation.
  if (normalizedValue === 'copilot' && item.actorIsCopilot !== undefined) return item.actorIsCopilot
  if (!item.user) return false
  const displayLogin = item.user.displayLogin.toLowerCase()
  return (
    value === String(item.user.id) ||
    (normalizedValue === 'copilot' ? copilotActorDisplayLogins.has(displayLogin) : normalizedValue === displayLogin)
  )
}

export const activityTypes: Array<{group: TimelineActivityGroupKey; value: string; displayName: string}> = [
  {group: 'commits', value: 'commits', displayName: 'Commits'},
  {group: 'baseForce', value: 'base-force', displayName: 'Base changes and force pushes'},
  {group: 'approvals', value: 'approvals', displayName: 'Approvals'},
  {group: 'changesRequested', value: 'changes-requested', displayName: 'Changes requested'},
  {group: 'comments', value: 'comments', displayName: 'Commented reviews'},
  {group: 'issueComments', value: 'issue-comments', displayName: 'Conversation comments'},
  {group: 'state', value: 'state', displayName: 'State changes'},
  {group: 'labels', value: 'labels', displayName: 'Labels'},
  {group: 'reviewRequests', value: 'review-requests', displayName: 'Review requests'},
  {group: 'description', value: 'description', displayName: 'Description edits'},
]

const sinceTokens: Record<SinceSource, string> = {
  [SinceSource.Created]: 'created',
  [SinceSource.LastReview]: 'last-review',
  [SinceSource.LastInteracted]: 'last-interacted',
  [SinceSource.ReadyForReview]: 'ready-for-review',
}

export function knownSinceValues(metadata: TimelinePanelMetadata | null) {
  const values = new Map(
    metadata?.timelineSinceSelectors?.map(selector => [sinceTokens[selector.source], selector.timestamp]),
  )
  const group = metadata?.activityGroup
  // Preserve the exact cutoff captured by the row, even if another selector snapshot differs.
  if (group?.since && group.sinceSource) values.set(sinceTokens[group.sinceSource], group.since)
  return values
}

export function initialTimelineQuery(metadata: TimelinePanelMetadata | null): string {
  const group = metadata?.activityGroup
  if (!group) return ''
  const since = group.sinceSource ? sinceTokens[group.sinceSource] : group.since
  const sinceClause = group.since && since ? ` since:${since}` : ''
  // Actor-scoped rather than type-scoped: it cuts across every `type:`, so
  // seed the editable query with `actor:` instead of a `type:` qualifier.
  if (group.group === 'copilotActivity') return `actor:copilot${sinceClause}`
  const type = activityTypes.find(activity => activity.group === group.group)
  return `type:${type?.value ?? group.group}${sinceClause}`
}

const providerOptions = {
  filterTypes: {exclusive: false, valueless: false, hasValue: false},
}

export function timelineFilterProviders(metadata: TimelinePanelMetadata | null) {
  return [
    new SelectFilterProvider(
      {key: 'type', displayName: 'Activity type', priority: 1, icon: ListUnorderedIcon},
      activityTypes.map(type => ({...type, priority: 1})),
      providerOptions,
    ),
    new StaticFilterProvider(
      {key: 'actor', displayName: 'Actor login or ID', priority: 2, icon: PersonIcon},
      [],
      providerOptions,
    ),
    new StaticFilterProvider(
      {key: 'since', displayName: 'Since', priority: 3, icon: ClockIcon},
      [...knownSinceValues(metadata)].map(([value]) => ({value, displayName: value, priority: 1})),
      {...providerOptions, filterTypes: {...providerOptions.filterTypes, multiValue: false}},
    ),
  ]
}

export function matchesTimelineSince(item: TimelineItem, since: string) {
  // Match the existing activity group behavior for events without a timestamp.
  return !item.timestamp || new Date(item.timestamp) >= new Date(since)
}

function searchableTimelineText(item: TimelineItem) {
  const info = item.additionalInfo?.info
  return [
    item.user?.displayLogin ?? '',
    getTimelineItemAutomation(item)?.name ?? '',
    ...(info?.case === 'pullRequestCommit' ? [info.value.shortMessageText ?? '', info.value.sha] : []),
  ].map(value => value.toLowerCase())
}

export function shouldAutoLoadTimelineScope({
  activityGroup,
  isQueryEdited,
  isQueryValid,
  hasNextPage,
  isFetchNextPageError,
  matchingCount,
}: {
  activityGroup: TimelinePanelMetadata['activityGroup']
  isQueryEdited: boolean
  isQueryValid: boolean
  hasNextPage: boolean
  isFetchNextPageError: boolean
  matchingCount: number
}) {
  // Only the untouched Overview scope can use its summary count as a pagination bound.
  return (
    activityGroup !== undefined &&
    !isQueryEdited &&
    isQueryValid &&
    hasNextPage &&
    !isFetchNextPageError &&
    (activityGroup.expectedCount == null || matchingCount < activityGroup.expectedCount)
  )
}

function timelineQueryBlocks(raw: string, metadata: TimelinePanelMetadata | null) {
  return new FilterQueryParser(timelineFilterProviders(metadata), {
    ...defaultFilterConfig,
    groupAndKeywordSupport: true,
  }).parse(raw).blocks
}

type TimelineQualifier = 'type' | 'actor' | 'since'

export function timelineQualifierValues(raw: string, metadata: TimelinePanelMetadata | null) {
  const values: Record<TimelineQualifier, string[]> = {type: [], actor: [], since: []}
  for (const block of timelineQueryBlocks(raw, metadata)) {
    if (!isFilterBlock(block) || (block.operator !== FilterOperator.Is && block.operator !== FilterOperator.IsOneOf)) {
      continue
    }
    const key = block.provider.key
    if (key === 'type' || key === 'actor' || key === 'since') {
      values[key].push(...block.value.values.map(value => getUnescapedFilterValue(value.value) ?? ''))
    }
  }
  return values
}

export function updateTimelineQualifier(
  raw: string,
  metadata: TimelinePanelMetadata | null,
  key: TimelineQualifier,
  values: string[],
) {
  const blocks = timelineQueryBlocks(raw, metadata)
  const isTarget = (block: (typeof blocks)[number] | undefined) =>
    block && isFilterBlock(block) && block.provider.key === key
  const remaining = blocks
    .filter((block, index) => !isTarget(block) && !(block.type === BlockType.Space && isTarget(blocks[index + 1])))
    .map(block => block.raw)
    .join('')
    .trim()
  const qualifier = values.length > 0 ? `${key}:${values.map(escapeFilterValue).join(',')}` : ''
  return [remaining, qualifier].filter(Boolean).join(' ')
}

export function parseTimelineQuery(raw: string, metadata: TimelinePanelMetadata | null) {
  const blocks = timelineQueryBlocks(raw, metadata)
  const predicates: Array<(item: TimelineItem) => boolean> = []
  const textTerms: string[] = []
  const errors: string[] = []
  for (const block of blocks) {
    if (block.type === BlockType.Space) continue
    if (block.type === BlockType.Text) {
      if (!block.raw.startsWith('"') && (block.raw.includes(':') || block.raw.startsWith('-'))) {
        errors.push('Use type:, actor:, or since: qualifiers. Quote literal text containing colons or leading hyphens.')
      } else {
        const text = getUnescapedFilterValue(block.raw)?.toLowerCase()
        if (text) textTerms.push(text)
      }
      continue
    }
    if (!isFilterBlock(block)) {
      errors.push(
        'Parentheses, AND, and OR are not supported. Use plain text, quoted phrases, or type:, actor:, and since:.',
      )
      continue
    }
    if (block.operator !== FilterOperator.Is && block.operator !== FilterOperator.IsOneOf) {
      errors.push('Only inclusive qualifiers are supported. Remove exclusions, comparisons, or ranges.')
      continue
    }
    const values = block.value.values.map(value => getUnescapedFilterValue(value.value) ?? '')
    if (values.length === 0 || values.some(value => !value)) {
      errors.push(`Enter a value for ${block.provider.key}:.`)
      continue
    }
    switch (block.provider.key) {
      case 'type': {
        const types = values.map(value => activityTypes.find(type => type.value === value.toLowerCase()))
        if (types.some(type => !type)) {
          errors.push('Unknown activity type. Choose a type from the suggestions.')
        } else {
          const typePredicates = types.flatMap(type => (type ? [groupPredicate(type.group)] : []))
          predicates.push(item => typePredicates.some(predicate => predicate(item)))
        }
        break
      }
      case 'actor':
        if (
          values.some(value => ['>', '<', '='].some(operator => value.startsWith(operator)) || value.includes('..'))
        ) {
          errors.push('Use an actor login or ID, not a comparison or range.')
          break
        }
        predicates.push(item => values.some(value => matchesActor(item, value)))
        break
      case 'since': {
        const value = values[0] ?? ''
        const timestamp = knownSinceValues(metadata).get(value.toLowerCase()) ?? value
        const date = new Date(timestamp)
        // Named presets must come from Overview. Otherwise require an explicit ISO timestamp.
        if (values.length !== 1 || !timestamp.includes('T') || !Number.isFinite(date.getTime())) {
          errors.push('Use an available since: preset or an ISO timestamp, such as 2026-09-08T17:45:00Z.')
        } else {
          predicates.push(item => matchesTimelineSince(item, timestamp))
        }
        break
      }
    }
  }
  if (textTerms.length > 0) {
    predicates.push(item => {
      const fields = searchableTimelineText(item)
      return textTerms.every(term => fields.some(field => field.includes(term)))
    })
  }
  return {
    errors: [...new Set(errors)],
    matches: (item: TimelineItem) => errors.length === 0 && predicates.every(predicate => predicate(item)),
  }
}
